import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { ArrowRight, MessageCircle, Mail, Phone, CheckCircle2, Sparkles, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { companyDivisions, featuredProducts, services, whyChooseUs, teamMembers, contactInfo } from '../mock';

const Home = () => {
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [isCompaniesModalOpen, setIsCompaniesModalOpen] = useState(false);
  const [expandedCompany, setExpandedCompany] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: ''
  });
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [formSuccess, setFormSuccess] = useState(false);

  const slides = [
    {
      title: "Your Trusted Global Partner",
      subtitle: "Delivering excellence across continents with reliable sourcing and supply",
      image: "https://customer-assets.emergentagent.com/job_premium-sourcing-7/artifacts/x5wjcx0i_285590a0-31f8-4d7b-85e6-f7e1bd33db84.png"
    },
    {
      title: "Global Trading • Supply • Solutions",
      subtitle: "Connecting international markets through energy, industry, technology & commodities",
      image: "https://customer-assets.emergentagent.com/job_premium-sourcing-7/artifacts/4sqrmp28_f55ac319-ec6a-4b66-86a2-6551824e0d22.png"
    },
    {
      title: "Multi-Industry Expertise Across Continents",
      subtitle: "Serving clients in UAE, UK, and USA with comprehensive trade solutions",
      image: "https://customer-assets.emergentagent.com/job_premium-sourcing-7/artifacts/x88flslo_b15879be-c08c-4f68-aa2c-1b79f101ddc5.png"
    }
  ];

  useEffect(() => {
    setIsVisible(true);
    
    // Auto-slide every 5 seconds
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);
    
    return () => clearInterval(interval);
  }, []);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const handleContact = (company) => {
    const message = `Hello ${company.shortName}, I would like to inquire about your services.`;
    const whatsappUrl = `https://wa.me/${company.phone.replace(/[^0-9]/g, '')}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setFormSubmitting(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setFormSuccess(true);
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          message: ''
        });
        setTimeout(() => setFormSuccess(false), 5000);
      }
    } catch (error) {
      console.error('Form submission error:', error);
    } finally {
      setFormSubmitting(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  // Slide-in animation state for stats
  const [statsVisible, setStatsVisible] = useState(false);
  const statsRef = React.useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setStatsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (statsRef.current) {
      observer.observe(statsRef.current);
    }

    return () => {
      if (statsRef.current) {
        observer.unobserve(statsRef.current);
      }
    };
  }, []);

  // Count-up animation component
  const CountUp = ({ end, duration = 2000, suffix = '' }) => {
    const [count, setCount] = useState(0);
    const [hasAnimated, setHasAnimated] = useState(false);

    useEffect(() => {
      if (statsVisible && !hasAnimated) {
        setHasAnimated(true);
        let startTime = null;
        const animate = (timestamp) => {
          if (!startTime) startTime = timestamp;
          const progress = Math.min((timestamp - startTime) / duration, 1);
          setCount(Math.floor(progress * end));
          if (progress < 1) {
            requestAnimationFrame(animate);
          }
        };
        requestAnimationFrame(animate);
      }
    }, [statsVisible, hasAnimated, end, duration]);

    return <>{count}{suffix}</>;
  };

  return (
    <>
      <Helmet>
        <title>Eaglesway Group | Global Trading & Industrial Supply</title>
        <meta 
          name="description" 
          content="Eaglesway Group provides international trading, procurement, logistics and industrial supply solutions across the UAE, United Kingdom and United States." 
        />
        <meta name="keywords" content="international trading, global procurement, industrial supply, logistics, UAE business, UK trading, USA supply chain" />
      </Helmet>
      
      <div className="min-h-screen bg-black text-white">
        {/* Premium Hero Carousel - Auto-Sliding */}
        <section className="relative h-screen min-h-[600px] overflow-hidden">
          {/* Slides */}
          {slides.map((slide, index) => (
            <div
              key={index}
              className={`absolute inset-0 transition-opacity duration-1000 ${
                index === currentSlide ? 'opacity-100' : 'opacity-0'
              }`}
            >
              {/* Background Image */}
              <div className="absolute inset-0">
                <img
                  src={slide.image}
                  alt={slide.title}
                  className="w-full h-full object-cover"
                  style={{
                    objectPosition: 'center center',
                    imageRendering: 'high-quality'
                  }}
                />
                {/* Navy Blue Gradient Overlay */}
                <div 
                  className="absolute inset-0"
                  style={{
                    background: 'linear-gradient(rgba(10,26,47,0.7), rgba(10,26,47,0.9))'
                  }}
                ></div>
              </div>

              {/* Content */}
              <div className={`relative z-10 h-full flex items-center justify-center transition-all duration-1000 ${
                index === currentSlide ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}>
                <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                  <div className="mb-6 inline-flex items-center px-4 py-2 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-full">
                    <Sparkles className="w-4 h-4 text-[#D4AF37] mr-2" />
                    <span className="text-[#D4AF37] text-sm font-medium">{slide.title}</span>
                  </div>
                  
                  <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
                    <span className="text-white">EAGLES</span>
                    <span className="text-[#D4AF37]">WAY</span>
                    <span className="text-white"> GROUP</span>
                  </h1>
                  
                  <p className="text-xl md:text-2xl text-gray-300 mb-10 font-light max-w-4xl mx-auto leading-relaxed">
                    {slide.subtitle}
                  </p>
                  
                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <Button
                      onClick={() => setIsCompaniesModalOpen(true)}
                      className="bg-[#D4AF37] hover:bg-[#B8941F] text-black font-semibold px-10 py-7 text-lg transition-all duration-300 transform hover:scale-105 shadow-2xl"
                    >
                      Explore Our Companies
                      <ArrowRight className="ml-2" />
                    </Button>
                    <Button
                      onClick={() => navigate('/quote')}
                      variant="outline"
                      className="border-2 border-[#D4AF37] text-[#D4AF37] bg-black/50 backdrop-blur-sm hover:bg-[#D4AF37] hover:text-black font-semibold px-10 py-7 text-lg transition-all duration-300"
                    >
                      Request a Quote
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ))}

          {/* Navigation Arrows */}
          <button
            onClick={prevSlide}
            className="absolute left-4 top-1/2 -translate-y-1/2 z-20 bg-black/50 hover:bg-[#D4AF37]/20 border border-[#D4AF37]/30 hover:border-[#D4AF37] text-white p-3 rounded-full transition-all duration-300 backdrop-blur-sm"
            aria-label="Previous slide"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          <button
            onClick={nextSlide}
            className="absolute right-4 top-1/2 -translate-y-1/2 z-20 bg-black/50 hover:bg-[#D4AF37]/20 border border-[#D4AF37]/30 hover:border-[#D4AF37] text-white p-3 rounded-full transition-all duration-300 backdrop-blur-sm"
            aria-label="Next slide"
          >
            <ChevronRight className="w-6 h-6" />
          </button>

          {/* Slide Indicators */}
          <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-20 flex gap-2">
            {slides.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentSlide(index)}
                className={`w-2 h-2 rounded-full transition-all duration-300 ${
                  index === currentSlide
                    ? 'bg-[#D4AF37] w-8'
                    : 'bg-white/50 hover:bg-white/80'
                }`}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>

          {/* Scroll Indicator */}
          <div className="absolute bottom-20 left-1/2 transform -translate-x-1/2 animate-bounce">
            <div className="w-6 h-10 border-2 border-[#D4AF37] rounded-full flex items-start justify-center p-2">
              <div className="w-1 h-3 bg-[#D4AF37] rounded-full"></div>
            </div>
          </div>
        </section>

        {/* Enhanced Stats Section */}
        <section ref={statsRef} className="py-24 bg-gradient-to-b from-black via-gray-900 to-black relative overflow-hidden">
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-5">
            <div className="absolute inset-0" style={{backgroundImage: 'radial-gradient(circle, #D4AF37 1px, transparent 1px)', backgroundSize: '40px 40px'}}></div>
          </div>

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            {/* Section Header */}
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
                Your Trusted <span className="text-[#D4AF37]">Global Partner</span>
              </h2>
              <p className="text-gray-400 text-lg max-w-3xl mx-auto">
                Delivering excellence across continents with reliable, multi-industry operations
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
              {/* Stat 1 - Years Experience */}
              <div className="group bg-gradient-to-b from-gray-900/50 to-black/50 border-2 border-gray-800 hover:border-[#D4AF37] rounded-2xl p-8 transition-all duration-500 hover:-translate-y-2">
                <div className="text-center">
                  <div className="text-5xl md:text-6xl font-bold text-[#D4AF37] mb-3 transition-transform duration-300 group-hover:scale-110">
                    <CountUp end={5} suffix="+" />
                  </div>
                  <p className="text-white text-lg font-semibold mb-2">Years Experience</p>
                  <p className="text-gray-400 text-sm">Industry Leadership</p>
                </div>
              </div>

              {/* Stat 2 - Continents */}
              <div className="group bg-gradient-to-b from-gray-900/50 to-black/50 border-2 border-gray-800 hover:border-[#D4AF37] rounded-2xl p-8 transition-all duration-500 hover:-translate-y-2">
                <div className="text-center">
                  <div className="text-5xl md:text-6xl font-bold text-[#D4AF37] mb-3 transition-transform duration-300 group-hover:scale-110">
                    <CountUp end={3} />
                  </div>
                  <p className="text-white text-lg font-semibold mb-2">Continents</p>
                  <p className="text-gray-400 text-sm">Global Reach</p>
                </div>
              </div>

              {/* Stat 3 - Industries Served */}
              <div className="group bg-gradient-to-b from-gray-900/50 to-black/50 border-2 border-gray-800 hover:border-[#D4AF37] rounded-2xl p-8 transition-all duration-500 hover:-translate-y-2">
                <div className="text-center">
                  <div className="text-5xl md:text-6xl font-bold text-[#D4AF37] mb-3 transition-transform duration-300 group-hover:scale-110">
                    <CountUp end={9} suffix="+" />
                  </div>
                  <p className="text-white text-lg font-semibold mb-2">Industries Served</p>
                  <p className="text-gray-400 text-sm">Diverse Sectors</p>
                </div>
              </div>

              {/* Stat 4 - 24/7 Support */}
              <div className="group bg-gradient-to-b from-gray-900/50 to-black/50 border-2 border-gray-800 hover:border-[#D4AF37] rounded-2xl p-8 transition-all duration-500 hover:-translate-y-2">
                <div className="text-center">
                  <div className="text-5xl md:text-6xl font-bold text-[#D4AF37] mb-3 transition-transform duration-300 group-hover:scale-110">
                    24/7
                  </div>
                  <p className="text-white text-lg font-semibold mb-2">Global Support</p>
                  <p className="text-gray-400 text-sm">Always Available</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Services Section */}
        <section className="py-24 bg-black">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
                Our <span className="text-[#D4AF37]">Services</span>
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { title: 'Global Sourcing & Procurement', icon: '🌐' },
                { title: 'Import & Export', icon: '📦' },
                { title: 'Supply Chain Solutions', icon: '🚚' },
                { title: 'Industrial & Commercial Support', icon: '🏭' }
              ].map((service, idx) => (
                <Card key={idx} className="bg-gradient-to-b from-gray-900 to-black border-2 border-gray-800 hover:border-[#D4AF37] p-8 text-center transition-all duration-300 hover:-translate-y-2">
                  <div className="text-5xl mb-4">{service.icon}</div>
                  <h3 className="text-white font-bold text-lg">{service.title}</h3>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Product Categories */}
        <section className="py-24 bg-gradient-to-b from-black to-gray-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
                Product <span className="text-[#D4AF37]">Categories</span>
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
              {[
                'Electronics & Technology',
                'Agricultural Commodities',
                'Industrial Machinery',
                'Vehicles & Automotive',
                'Oil & Gas Supply',
                'Construction Materials'
              ].map((category, idx) => (
                <Card key={idx} className="bg-gradient-to-b from-gray-900 to-black border-2 border-gray-800 hover:border-[#D4AF37] p-6 transition-all duration-300 hover:-translate-y-2">
                  <h3 className="text-white font-semibold text-lg">{category}</h3>
                </Card>
              ))}
            </div>

            <div className="text-center">
              <Button
                onClick={() => navigate('/products')}
                className="bg-[#D4AF37] hover:bg-[#B8941F] text-black font-semibold px-12 py-6 text-lg"
              >
                View All Products
                <ArrowRight className="ml-2" />
              </Button>
            </div>
          </div>
        </section>


        {/* Partners & Customers - Infinite Auto-Scroll Slider */}
        <section className="py-20 bg-gradient-to-b from-black via-gray-900 to-black overflow-hidden">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-12">
            <div className="text-center">
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
                Trusted by Leading <span className="text-[#D4AF37]">Partners & Clients</span> Worldwide
              </h2>
            </div>
          </div>

          {/* Infinite Scroll Container */}
          <div className="relative">
            <div className="flex gap-8 animate-scroll hover:pause-animation">
              {/* First set of logos */}
              {[
                { src: "https://customer-assets.emergentagent.com/job_premium-sourcing-7/artifacts/tz0okp3n_WhatsApp%20Image%202026-03-27%20at%203.12.39%20PM.jpeg", alt: "HBS Foodstuff Trading", type: "image" },
                { src: "https://customer-assets.emergentagent.com/job_premium-sourcing-7/artifacts/ngfn8lxk_WhatsApp%20Image%202026-03-27%20at%203.12.43%20PM.jpeg", alt: "Yo Holding", type: "image" },
                { src: "https://customer-assets.emergentagent.com/job_premium-sourcing-7/artifacts/4bsa8h9m_WhatsApp%20Image%202026-03-27%20at%203.13.02%20PM.jpeg", alt: "Partner", type: "image" },
                { src: "https://customer-assets.emergentagent.com/job_premium-sourcing-7/artifacts/9z04q8cs_WhatsApp%20Image%202026-03-27%20at%203.12.58%20PM.jpeg", alt: "Niiks Foods", type: "image" },
                { src: "https://customer-assets.emergentagent.com/job_premium-sourcing-7/artifacts/op4bwf4o_WhatsApp%20Image%202026-03-27%20at%203.15.37%20PM.jpeg", alt: "Quartz Elec", type: "image" },
                { src: "https://customer-assets.emergentagent.com/job_premium-sourcing-7/artifacts/5yztgm8o_WhatsApp%20Image%202026-03-27%20at%202.56.35%20PM.jpeg", alt: "Al Najah General Trading", type: "image" },
                { src: "https://customer-assets.emergentagent.com/job_premium-sourcing-7/artifacts/ic20fx6r_WhatsApp%20Image%202026-03-27%20at%202.56.35%20PM%20%282%29.jpeg", alt: "Basilea", type: "image" },
                { src: "https://customer-assets.emergentagent.com/job_premium-sourcing-7/artifacts/a7ueggjt_WhatsApp%20Image%202026-03-27%20at%202.56.35%20PM%20%281%29.jpeg", alt: "Kumneger Trading PLC", type: "image" },
                { src: "https://customer-assets.emergentagent.com/job_premium-sourcing-7/artifacts/4m5ssvei_WhatsApp%20Image%202026-03-27%20at%202.56.34%20PM.jpeg", alt: "Saham Al Najah Marketing", type: "image" },
                { src: "https://customer-assets.emergentagent.com/job_premium-sourcing-7/artifacts/qsrs0jgz_WhatsApp%20Image%202026-03-30%20at%207.07.15%20PM.jpeg", alt: "Rakez", type: "image" },
                { src: "https://customer-assets.emergentagent.com/job_premium-sourcing-7/artifacts/95w3dmvf_sfoor.jpeg", alt: "SJFLOR", type: "image" },
                { alt: "Meleyone", type: "text" },
                { alt: "Sana", type: "text" },
                { alt: "Ameyou", type: "text" },
                { alt: "Meron", type: "text" },
                { alt: "Eaglesway AI", type: "text" },
              ].concat([
                { src: "https://customer-assets.emergentagent.com/job_premium-sourcing-7/artifacts/tz0okp3n_WhatsApp%20Image%202026-03-27%20at%203.12.39%20PM.jpeg", alt: "HBS Foodstuff Trading", type: "image" },
                { src: "https://customer-assets.emergentagent.com/job_premium-sourcing-7/artifacts/ngfn8lxk_WhatsApp%20Image%202026-03-27%20at%203.12.43%20PM.jpeg", alt: "Yo Holding", type: "image" },
                { src: "https://customer-assets.emergentagent.com/job_premium-sourcing-7/artifacts/4bsa8h9m_WhatsApp%20Image%202026-03-27%20at%203.13.02%20PM.jpeg", alt: "Partner", type: "image" },
                { src: "https://customer-assets.emergentagent.com/job_premium-sourcing-7/artifacts/9z04q8cs_WhatsApp%20Image%202026-03-27%20at%203.12.58%20PM.jpeg", alt: "Niiks Foods", type: "image" },
                { src: "https://customer-assets.emergentagent.com/job_premium-sourcing-7/artifacts/op4bwf4o_WhatsApp%20Image%202026-03-27%20at%203.15.37%20PM.jpeg", alt: "Quartz Elec", type: "image" },
                { src: "https://customer-assets.emergentagent.com/job_premium-sourcing-7/artifacts/5yztgm8o_WhatsApp%20Image%202026-03-27%20at%202.56.35%20PM.jpeg", alt: "Al Najah General Trading", type: "image" },
                { src: "https://customer-assets.emergentagent.com/job_premium-sourcing-7/artifacts/ic20fx6r_WhatsApp%20Image%202026-03-27%20at%202.56.35%20PM%20%282%29.jpeg", alt: "Basilea", type: "image" },
                { src: "https://customer-assets.emergentagent.com/job_premium-sourcing-7/artifacts/a7ueggjt_WhatsApp%20Image%202026-03-27%20at%202.56.35%20PM%20%281%29.jpeg", alt: "Kumneger Trading PLC", type: "image" },
                { src: "https://customer-assets.emergentagent.com/job_premium-sourcing-7/artifacts/4m5ssvei_WhatsApp%20Image%202026-03-27%20at%202.56.34%20PM.jpeg", alt: "Saham Al Najah Marketing", type: "image" },
                { src: "https://customer-assets.emergentagent.com/job_premium-sourcing-7/artifacts/qsrs0jgz_WhatsApp%20Image%202026-03-30%20at%207.07.15%20PM.jpeg", alt: "Rakez", type: "image" },
                { src: "https://customer-assets.emergentagent.com/job_premium-sourcing-7/artifacts/95w3dmvf_sfoor.jpeg", alt: "SJFLOR", type: "image" },
                { alt: "Meleyone", type: "text" },
                { alt: "Sana", type: "text" },
                { alt: "Ameyou", type: "text" },
                { alt: "Meron", type: "text" },
                { alt: "Eaglesway AI", type: "text" },
              ]).map((logo, index) => (
                <div
                  key={index}
                  className="flex-shrink-0 w-64 h-32 flex items-center justify-center p-6 bg-white/10 rounded-lg border border-gray-800 hover:border-[#D4AF37] transition-all duration-300 group hover:bg-white/15"
                >
                  {logo.type === "image" ? (
                    <img
                      src={logo.src}
                      alt={logo.alt}
                      className="max-w-full max-h-full object-contain transition-all duration-300"
                      style={{mixBlendMode: 'lighten'}}
                    />
                  ) : (
                    <div className="text-center">
                      <h3 className="text-[#D4AF37] font-bold text-2xl group-hover:scale-110 transition-transform duration-300">
                        {logo.alt}
                      </h3>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Request a Quote Button */}
          <div className="text-center mt-12">
            <Button
              onClick={() => navigate('/quote')}
              className="bg-[#D4AF37] hover:bg-[#B8941F] text-black font-semibold px-12 py-6 text-lg transition-all duration-300 transform hover:scale-105 shadow-2xl"
            >
              Request a Quote
              <ArrowRight className="ml-2" />
            </Button>
          </div>
        </section>

        {/* Contact / Request a Quote CTA */}
        <section className="py-24 bg-gradient-to-b from-black via-gray-900 to-black">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <Card className="relative bg-gradient-to-br from-[#D4AF37]/10 via-black to-black border-2 border-[#D4AF37] p-12 md:p-16 overflow-hidden">
              {/* Background Pattern */}
              <div className="absolute inset-0 opacity-5">
                <div className="absolute inset-0" style={{backgroundImage: 'linear-gradient(45deg, #D4AF37 25%, transparent 25%, transparent 75%, #D4AF37 75%, #D4AF37), linear-gradient(45deg, #D4AF37 25%, transparent 25%, transparent 75%, #D4AF37 75%, #D4AF37)', backgroundSize: '30px 30px', backgroundPosition: '0 0, 15px 15px'}}></div>
              </div>

              <div className="relative z-10 text-center">
                <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">
                  Ready to Partner with <span className="text-[#D4AF37]">Eaglesway</span>?
                </h2>
                <p className="text-gray-300 text-lg mb-10 max-w-3xl mx-auto leading-relaxed">
                  Join us in delivering excellence across global markets. Whether you need sourcing, logistics, or supply solutions, we're here to help your business succeed.
                </p>
                
                <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
                  <Button
                    onClick={() => navigate('/quote')}
                    className="bg-[#D4AF37] hover:bg-[#B8941F] text-black font-semibold px-12 py-7 text-lg transition-all duration-300 transform hover:scale-105 shadow-2xl"
                  >
                    Join Us
                  </Button>
                  <Button
                    onClick={() => navigate('/contact')}
                    variant="outline"
                    className="border-2 border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black font-semibold px-12 py-7 text-lg transition-all duration-300"
                  >
                    Contact Us
                  </Button>
                </div>

                <div className="flex flex-wrap justify-center gap-8 text-sm text-gray-400">
                  <div className="flex items-center">
                    <Phone className="w-4 h-4 mr-2 text-[#D4AF37]" />
                    {contactInfo.phone}
                  </div>
                  <div className="flex items-center">
                    <Mail className="w-4 h-4 mr-2 text-[#D4AF37]" />
                    {contactInfo.email}
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </section>

        {/* Companies Modal - Full Screen */}
        {isCompaniesModalOpen && (
          <div className="fixed inset-0 bg-black/90 backdrop-blur-md z-50 flex items-center justify-center p-4 animate-in fade-in duration-300">
            {/* Modal Content */}
            <div className="relative bg-gradient-to-b from-gray-900 to-black border-2 border-[#D4AF37] rounded-2xl w-full max-w-6xl max-h-[90vh] overflow-y-auto animate-in slide-in-from-bottom duration-500">
              {/* Close Button */}
              <button
                onClick={() => {
                  setIsCompaniesModalOpen(false);
                  setExpandedCompany(null);
                }}
                className="sticky top-4 right-4 float-right z-10 text-gray-400 hover:text-[#D4AF37] transition-colors duration-300 bg-black/50 rounded-full p-2"
              >
                <X className="w-8 h-8" />
              </button>

              <div className="p-8 md:p-12">
                {/* Header */}
                <div className="text-center mb-12">
                  <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
                    Our <span className="text-[#D4AF37]">Companies</span>
                  </h2>
                  <p className="text-gray-400 text-lg max-w-3xl mx-auto">
                    Explore the strength and specialization of Eaglesway Group across global markets
                  </p>
                </div>

                {/* Company Cards Grid */}
                {!expandedCompany ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {companyDivisions.map((company) => (
                      <Card
                        key={company.id}
                        className="group bg-gradient-to-b from-gray-800/50 to-black border-2 border-gray-700 hover:border-[#D4AF37] transition-all duration-300 p-6"
                      >
                        {/* Company Logo */}
                        <div className="mb-4 h-24 flex items-center justify-center">
                          <div className="relative w-full h-full flex items-center justify-center bg-gradient-to-br from-[#F5E6D3] via-[#E8D4B8] to-[#D4AF37] rounded-xl">
                            <img 
                              src={company.logo} 
                              alt={company.shortName}
                              className="max-h-20 max-w-[80%] object-contain"
                              style={{mixBlendMode: 'multiply'}}
                            />
                          </div>
                        </div>

                        {/* Country Badge */}
                        <div className="inline-block bg-[#D4AF37]/10 border border-[#D4AF37]/30 text-[#D4AF37] text-xs font-bold px-3 py-1 rounded-full mb-3">
                          {company.country}
                        </div>

                        {/* Company Info */}
                        <h3 className="text-white font-bold text-xl mb-2">
                          {company.company}
                        </h3>
                        
                        <p className="text-gray-400 text-sm mb-4 line-clamp-2">
                          {company.description}
                        </p>

                        <p className="text-gray-500 text-xs mb-4">
                          📍 {company.location}
                        </p>

                        {/* View Details Button */}
                        <Button
                          onClick={() => setExpandedCompany(company)}
                          className="w-full bg-[#D4AF37] hover:bg-[#B8941F] text-black font-semibold transition-all duration-300"
                        >
                          View Details
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                      </Card>
                    ))}
                  </div>
                ) : (
                  /* Expanded Company Details */
                  <div className="animate-in slide-in-from-right duration-500">
                    {/* Back Button */}
                    <button
                      onClick={() => setExpandedCompany(null)}
                      className="flex items-center text-[#D4AF37] hover:text-[#B8941F] mb-6 transition-colors"
                    >
                      <ChevronLeft className="w-5 h-5 mr-2" />
                      Back to All Companies
                    </button>

                    {/* Company Logo */}
                    <div className="mb-8 flex items-center justify-center">
                      <div className="w-64 h-40 bg-gradient-to-br from-[#F5E6D3] via-[#E8D4B8] to-[#D4AF37] rounded-xl p-8 shadow-lg">
                        <img 
                          src={expandedCompany.logo} 
                          alt={expandedCompany.shortName}
                          className="w-full h-full object-contain"
                          style={{mixBlendMode: 'multiply'}}
                        />
                      </div>
                    </div>

                    {/* Company Header */}
                    <div className="text-center mb-8">
                      <div className="inline-block bg-[#D4AF37]/10 border border-[#D4AF37]/30 text-[#D4AF37] text-sm font-bold px-4 py-2 rounded-full mb-4">
                        {expandedCompany.country}
                      </div>
                      <h2 className="text-3xl md:text-4xl font-bold text-white mb-2">
                        {expandedCompany.company}
                      </h2>
                      <p className="text-[#D4AF37] text-lg font-medium mb-4">
                        {expandedCompany.tagline}
                      </p>
                      <p className="text-gray-400 text-base max-w-3xl mx-auto">
                        {expandedCompany.description}
                      </p>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                      {/* Profile Section */}
                      <Card className="bg-black/50 border-2 border-gray-800 hover:border-[#D4AF37]/50 p-6 transition-colors">
                        <h3 className="text-xl font-bold text-white mb-4 flex items-center">
                          <span className="w-1 h-6 bg-[#D4AF37] mr-3"></span>
                          Profile
                        </h3>
                        <p className="text-gray-400 leading-relaxed">
                          {expandedCompany.description}
                        </p>
                      </Card>

                      {/* Activities Section */}
                      <Card className="bg-black/50 border-2 border-gray-800 hover:border-[#D4AF37]/50 p-6 transition-colors">
                        <h3 className="text-xl font-bold text-white mb-4 flex items-center">
                          <span className="w-1 h-6 bg-[#D4AF37] mr-3"></span>
                          Activities
                        </h3>
                        <ul className="space-y-2.5">
                          {(expandedCompany.activities || expandedCompany.services.slice(0, 5)).map((activity, idx) => (
                            <li key={idx} className="flex items-start text-gray-400 text-sm">
                              <CheckCircle2 className="w-4 h-4 text-[#D4AF37] mr-2 flex-shrink-0 mt-0.5" />
                              <span>{activity}</span>
                            </li>
                          ))}
                        </ul>
                      </Card>

                      {/* Services Section */}
                      <Card className="bg-black/50 border-2 border-gray-800 hover:border-[#D4AF37]/50 p-6 transition-colors">
                        <h3 className="text-xl font-bold text-white mb-4 flex items-center">
                          <span className="w-1 h-6 bg-[#D4AF37] mr-3"></span>
                          Services
                        </h3>
                        <ul className="space-y-2">
                          {expandedCompany.services.map((service, idx) => (
                            <li key={idx} className="flex items-start text-gray-400 text-sm">
                              <span className="text-[#D4AF37] mr-2">•</span>
                              <span>{service}</span>
                            </li>
                          ))}
                        </ul>
                      </Card>

                      {/* Address & Contact Section */}
                      <Card className="bg-black/50 border-2 border-gray-800 hover:border-[#D4AF37]/50 p-6 transition-colors">
                        <h3 className="text-xl font-bold text-white mb-4 flex items-center">
                          <span className="w-1 h-6 bg-[#D4AF37] mr-3"></span>
                          Address
                        </h3>
                        <div className="space-y-4 mb-6">
                          <div className="flex items-start text-gray-400">
                            <span className="text-[#D4AF37] mr-2 mt-1">📍</span>
                            <p className="whitespace-pre-line text-sm leading-relaxed">
                              {expandedCompany.fullAddress || expandedCompany.location}
                            </p>
                          </div>
                        </div>
                      </Card>
                    </div>

                    {/* Contact Information Full Width */}
                    <Card className="bg-gradient-to-br from-gray-900 to-black border-2 border-[#D4AF37] p-8">
                      <h3 className="text-2xl font-bold text-white mb-6 flex items-center">
                        <span className="w-1 h-8 bg-[#D4AF37] mr-3"></span>
                        Contact Information
                      </h3>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                        {/* Phone */}
                        {expandedCompany.phone && (
                          <div className="flex items-center text-gray-300">
                            <Phone className="w-5 h-5 text-[#D4AF37] mr-3" />
                            <a href={`tel:${expandedCompany.phone}`} className="hover:text-[#D4AF37] transition-colors">
                              {expandedCompany.phone}
                            </a>
                          </div>
                        )}

                        {/* Email */}
                        <div className="flex items-center text-gray-300 group">
                          <Mail className="w-5 h-5 text-[#D4AF37] mr-3" />
                          <a 
                            href={`mailto:${expandedCompany.email}`}
                            className="hover:text-[#D4AF37] transition-colors mr-2"
                          >
                            {expandedCompany.email}
                          </a>
                          <button
                            onClick={() => {
                              navigator.clipboard.writeText(expandedCompany.email);
                              alert('Email copied to clipboard!');
                            }}
                            className="opacity-0 group-hover:opacity-100 text-[#D4AF37] text-xs transition-opacity"
                          >
                            📋 Copy
                          </button>
                        </div>

                        {/* Sales Email */}
                        {expandedCompany.salesEmail && (
                          <div className="flex items-center text-gray-300 group">
                            <Mail className="w-5 h-5 text-[#D4AF37] mr-3" />
                            <div className="flex flex-col">
                              <span className="text-xs text-gray-500 mb-1">Sales:</span>
                              <a 
                                href={`mailto:${expandedCompany.salesEmail}`}
                                className="hover:text-[#D4AF37] transition-colors mr-2"
                              >
                                {expandedCompany.salesEmail}
                              </a>
                            </div>
                            <button
                              onClick={() => {
                                navigator.clipboard.writeText(expandedCompany.salesEmail);
                                alert('Sales email copied to clipboard!');
                              }}
                              className="opacity-0 group-hover:opacity-100 text-[#D4AF37] text-xs transition-opacity ml-2"
                            >
                              📋 Copy
                            </button>
                          </div>
                        )}
                      </div>

                      {/* Action Button */}
                      <Button
                        onClick={() => {
                          if (expandedCompany.phone) {
                            handleContact(expandedCompany);
                          } else {
                            window.location.href = `mailto:${expandedCompany.email}`;
                          }
                        }}
                        className="w-full bg-[#D4AF37] hover:bg-[#B8941F] text-black font-bold py-7 text-lg transition-all duration-300 transform hover:scale-105 shadow-2xl"
                      >
                        <MessageCircle className="w-5 h-5 mr-2" />
                        {expandedCompany.phone ? 'Contact This Office via WhatsApp' : 'Contact This Office via Email'}
                      </Button>
                    </Card>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default Home;
